/**
 * MemberMeService
 * Self-service Member endpoints scoped to the currently authenticated user.
 * Backed by `Controllers/MeController.cs` in CCMS.Api.MemberManagement.
 *
 * Routes:
 *  - GET    api/members/me                  → Member
 *  - PUT    api/members/me                  → Member (partial: null clears, omitted unchanged)
 *  - GET    api/members/me/profile-status   → ProfileStatus
 *  - api/members/me/allergies | disabilities | addresses   (CRUD)
 *  - GET    api/members/me/family           → MemberFamilyView[]
 *  - GET/PUT api/members/me/locations       → MemberLocationView[]
 *  - api/members/me/emergency-contacts      (CRUD)
 */
import { BaseService } from '../base/BaseService';
export class MemberMeService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? MemberMeService.DEFAULT_SERVICE_PATH,
            basePath: MemberMeService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /** GET /api/members/me — current user's Member row. 404 if not yet provisioned. */
    async getMe() {
        const response = await this.getMeRaw();
        return response.data ?? null;
    }
    getMeRaw() {
        return this.http.get(this.buildUrl(''));
    }
    /** PUT /api/members/me — patch own profile. */
    async updateMe(payload) {
        const response = await this.updateMeRaw(payload);
        return response.data ?? null;
    }
    updateMeRaw(payload) {
        return this.http.put(this.buildUrl(''), payload);
    }
    /** GET /api/members/me/profile-status — onboarding gate. */
    async getProfileStatus() {
        const response = await this.getProfileStatusRaw();
        return response.data ?? { isComplete: false, missingFields: [] };
    }
    getProfileStatusRaw() {
        return this.http.get(this.buildUrl('profile-status'));
    }
    // ===== Allergies =====================================================
    async getMyAllergies() {
        const response = await this.http.get(this.buildUrl('allergies'));
        return response.data ?? [];
    }
    async addMyAllergy(payload) {
        const response = await this.http.post(this.buildUrl('allergies'), payload);
        return response.data ?? null;
    }
    async updateMyAllergy(allergyId, payload) {
        const response = await this.http.put(this.buildUrl(`allergies/${allergyId}`), payload);
        return response.data ?? null;
    }
    async deleteMyAllergy(allergyId) {
        const response = await this.http.delete(this.buildUrl(`allergies/${allergyId}`));
        return response.success;
    }
    // ===== Disabilities ==================================================
    async getMyDisabilities() {
        const response = await this.http.get(this.buildUrl('disabilities'));
        return response.data ?? [];
    }
    async addMyDisability(payload) {
        const response = await this.http.post(this.buildUrl('disabilities'), payload);
        return response.data ?? null;
    }
    async updateMyDisability(disabilityId, payload) {
        const response = await this.http.put(this.buildUrl(`disabilities/${disabilityId}`), payload);
        return response.data ?? null;
    }
    async deleteMyDisability(disabilityId) {
        const response = await this.http.delete(this.buildUrl(`disabilities/${disabilityId}`));
        return response.success;
    }
    // ===== Addresses =====================================================
    async getMyAddresses() {
        const response = await this.http.get(this.buildUrl('addresses'));
        return response.data ?? [];
    }
    async addMyAddress(payload) {
        const response = await this.http.post(this.buildUrl('addresses'), payload);
        return response.data ?? null;
    }
    async updateMyAddress(addressId, payload) {
        const response = await this.http.put(this.buildUrl(`addresses/${addressId}`), payload);
        return response.data ?? null;
    }
    async deleteMyAddress(addressId) {
        const response = await this.http.delete(this.buildUrl(`addresses/${addressId}`));
        return response.success;
    }
    // ===== Family (read-only) ============================================
    /** GET /api/members/me/family — the families I belong to, with everyone in them. */
    async getMyFamily() {
        const response = await this.http.get(this.buildUrl('family'));
        return response.data ?? [];
    }
    // ===== Campus ========================================================
    /** GET /api/members/me/locations — my home campus + serve location (0 = Online Campus). */
    async getMyLocations() {
        const response = await this.http.get(this.buildUrl('locations'));
        return response.data ?? [];
    }
    /**
     * PUT /api/members/me/locations — set my home campus (and serve location, defaulting to the
     * home campus). `locationID: null` removes it; 0 is "Online Campus".
     */
    async updateMyLocations(payload) {
        const response = await this.http.put(this.buildUrl('locations'), payload);
        return response.data ?? [];
    }
    // ===== Emergency contacts ============================================
    async getMyEmergencyContacts() {
        const response = await this.http.get(this.buildUrl('emergency-contacts'));
        return response.data ?? [];
    }
    async addMyEmergencyContact(payload) {
        const response = await this.http.post(this.buildUrl('emergency-contacts'), payload);
        return response.data ?? null;
    }
    async updateMyEmergencyContact(contactId, payload) {
        const response = await this.http.put(this.buildUrl(`emergency-contacts/${contactId}`), payload);
        return response.data ?? null;
    }
    async deleteMyEmergencyContact(contactId) {
        const response = await this.http.delete(this.buildUrl(`emergency-contacts/${contactId}`));
        return response.success;
    }
}
MemberMeService.DEFAULT_SERVICE_PATH = 'members';
MemberMeService.BASE_PATH = 'api/members/me';
//# sourceMappingURL=MemberMeService.js.map